export const SPLIT_WORKFLOW_TYPE = "split_workflow";

export function isWorkflowSection(field) {
  return field?.type === "section" || field?.type === SPLIT_WORKFLOW_TYPE;
}

export function isSplitWorkflowField(field) {
  return field?.type === SPLIT_WORKFLOW_TYPE;
}

export function getWorkflowConfig(field) {
  const config = field?.config || {};
  return {
    assignedUsers: Array.isArray(config.assignedUsers) ? config.assignedUsers : [],
    assignedGroups: Array.isArray(config.assignedGroups) ? config.assignedGroups : [],
    allUsers: config.allUsers !== false,
    allGroups: !!config.allGroups,
    previousSectionVisibility: config.previousSectionVisibility === "hidden" ? "hidden" : "visible",
  };
}

export function userMatchesWorkflow(field, userProfile) {
  if (!field || field.type !== SPLIT_WORKFLOW_TYPE) return true;

  const config = getWorkflowConfig(field);
  if (config.allUsers || config.allGroups) return true;

  const uid = userProfile?.uid || userProfile?.id || userProfile?.userId || null;
  const groups = Array.isArray(userProfile?.groups)
    ? userProfile.groups
    : userProfile?.group
      ? [userProfile.group]
      : [];

  if (uid && config.assignedUsers.includes(uid)) return true;
  if (groups.some((group) => config.assignedGroups.includes(group))) return true;

  return false;
}

export function canViewPreviousWorkflowSections(field) {
  return getWorkflowConfig(field).previousSectionVisibility === "visible";
}

export function flattenWorkflowSections(fields = []) {
  return fields.filter((field) => isWorkflowSection(field));
}

export function getSplitWorkflowSections(fields = []) {
  return (fields || []).filter((field) => isSplitWorkflowField(field));
}

export function hasSplitWorkflow(fields = []) {
  return getSplitWorkflowSections(fields).length > 0;
}

export function getFirstSplitWorkflowSection(fields = []) {
  return getSplitWorkflowSections(fields)[0] || null;
}

export function canStartWorkflowFromScratch(fields = [], userProfile) {
  const firstSection = getFirstSplitWorkflowSection(fields);
  if (!firstSection) return true;
  return userMatchesWorkflow(firstSection, userProfile);
}

function normalizeCompletedSections(sectionProgress) {
  return Array.isArray(sectionProgress?.completedSections)
    ? [...new Set(sectionProgress.completedSections.filter(Boolean))]
    : [];
}

export function getCurrentWorkflowSectionIndex(fields = [], sectionProgress = null) {
  const workflowSections = getSplitWorkflowSections(fields);
  if (!workflowSections.length) return -1;

  const rawIndex = Number(sectionProgress?.currentSectionIndex);
  if (!Number.isFinite(rawIndex)) {
    return 0;
  }

  return Math.min(Math.max(Math.floor(rawIndex), 0), workflowSections.length - 1);
}

export function getActiveWorkflowSection(fields = [], sectionProgress = null) {
  const workflowSections = getSplitWorkflowSections(fields);
  const activeIndex = getCurrentWorkflowSectionIndex(workflowSections, sectionProgress);
  if (activeIndex < 0) return null;
  return workflowSections[activeIndex] || null;
}

export function getNextWorkflowStep(fields = [], sectionProgress = null) {
  const workflowSections = getSplitWorkflowSections(fields);
  if (!workflowSections.length) {
    return {
      hasWorkflow: false,
      currentSection: null,
      nextSection: null,
      isFinalSection: true,
      nextProgress: sectionProgress || { currentSectionIndex: 0, completedSections: [] },
    };
  }

  const currentIndex = getCurrentWorkflowSectionIndex(workflowSections, sectionProgress);
  const currentSection = workflowSections[currentIndex] || null;
  const completedSections = normalizeCompletedSections(sectionProgress);
  const nextCompletedSections = currentSection?.id
    ? [...new Set([...completedSections, currentSection.id])]
    : completedSections;
  const nextIndex = currentIndex + 1;
  const nextSection = workflowSections[nextIndex] || null;
  const isFinalSection = !nextSection;

  return {
    hasWorkflow: true,
    currentSection,
    nextSection,
    isFinalSection,
    nextProgress: {
      currentSectionIndex: isFinalSection ? currentIndex : nextIndex,
      completedSections: nextCompletedSections,
    },
  };
}

export function isWorkflowDraftAccessibleToUser(draft, userProfile) {
  const uid = userProfile?.uid || userProfile?.id || userProfile?.userId || null;
  if (uid && draft?.createdBy === uid) {
    return true;
  }

  const activeSection = getActiveWorkflowSection(draft?.workflowSections || [], draft?.sectionProgress);
  if (!activeSection) {
    return false;
  }

  return userMatchesWorkflow(activeSection, userProfile);
}

export function getWorkflowVisibilityState(fields = [], userProfile, sectionProgress = null) {
  const splitWorkflowSections = getSplitWorkflowSections(fields);
  const workflowOrder = new Map(splitWorkflowSections.map((field, index) => [field.id, index]));
  const activeSection = getActiveWorkflowSection(splitWorkflowSections, sectionProgress);
  const activeIndex = activeSection ? workflowOrder.get(activeSection.id) ?? -1 : -1;
  const allowPrevious = activeSection ? canViewPreviousWorkflowSections(activeSection) : true;

  const visibleFieldIds = new Set();
  const readOnlyFieldIds = new Set();

  (fields || []).forEach((field) => {
    if (!field?.id) return;

    if (!isSplitWorkflowField(field)) {
      visibleFieldIds.add(field.id);
      return;
    }

    const fieldIndex = workflowOrder.get(field.id);
    if (fieldIndex === undefined) return;

    if (activeIndex >= 0) {
      if (fieldIndex > activeIndex) return;
      if (fieldIndex < activeIndex && !allowPrevious) return;
      if (fieldIndex === activeIndex && !userMatchesWorkflow(field, userProfile)) return;
      if (fieldIndex < activeIndex) {
        readOnlyFieldIds.add(field.id);
      }
    } else if (!userMatchesWorkflow(field, userProfile)) {
      return;
    }

    visibleFieldIds.add(field.id);
  });

  return {
    hideBeforeIndex: activeIndex >= 0 && !allowPrevious ? activeIndex : -1,
    activeSectionId: activeSection?.id || null,
    visibleFieldIds,
    readOnlyFieldIds,
    hiddenWorkflowCount: splitWorkflowSections.filter((field) => !visibleFieldIds.has(field.id)).length,
  };
}

export function filterVisibleWorkflowFields(fields = [], userProfile, sectionProgress = null) {
  const { visibleFieldIds } = getWorkflowVisibilityState(fields, userProfile, sectionProgress);
  return (fields || []).filter((field) => !field?.id || visibleFieldIds.has(field.id));
}

export function buildWorkflowSnapshot(fields = []) {
  return flattenWorkflowSections(fields).map((field, index) => ({
    id: field.id,
    label: field.label || field.question || `Section ${index + 1}`,
    type: field.type,
    order: index,
    config: getWorkflowConfig(field),
  }));
}
